import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserManagementStore } from '../stores/userManagementStore'
import { useSupabaseOrderStore } from '../stores/supabaseOrderStore'
import { useSupabaseBusStore } from '../stores/supabaseBusStore'
import { useSupabaseTestspielStore } from '../stores/supabaseTestspielStore'
import { useSupabaseTestspielDatesStore } from '../stores/supabaseTestspielDatesStore'
import AdminCodeVerification from '../components/AdminCodeVerification'
import { 
  ShoppingCart, 
  Bus, 
  Check, 
  X, 
  Package,
  Users,
  FileText,
  Calendar,
  Plus,
  Clock,
  Trash2
} from 'lucide-react'

export default function AdminPage() {
  const navigate = useNavigate()
  
  // User Management Store
  const { 
    users, 
    loading: usersLoading, 
    fetchUsers, 
    approveUser, 
    rejectUser,
    changeUserRole,
    deleteUser,
    getApprovedUsers
  } = useUserManagementStore()
  
  // Order management
  const { orders, fetchOrders, updateOrderStatus } = useSupabaseOrderStore()
  
  // Bus reservation management
  const { 
    reservations, 
    fetchReservations, 
    updateReservationStatus 
  } = useSupabaseBusStore()

  // Testspiel management
  const { 
    testspiele, 
    fetchTestspiele, 
    updateTestspielStatus 
  } = useSupabaseTestspielStore()

  // Load data when component mounts
  useEffect(() => {
    fetchUsers()
    fetchOrders()
    fetchReservations()
    fetchTestspiele()
  }, [])

  // Filter data
  const pendingOrders = orders.filter(o => o.status === 'pending')
  const pendingBusReservations = reservations.filter(r => r.status === 'pending')
  const pendingTestspiele = testspiele.filter(t => t.status === 'pending')
  const pendingUsers = users.filter(user => user.status === 'pending')
  const approvedUsers = getApprovedUsers()

  // Admin verification state
  const [showAdminVerification, setShowAdminVerification] = useState(false)
  const [pendingAction, setPendingAction] = useState<{
    type: 'order' | 'bus' | 'user' | 'testspiel' | 'role_change' | 'user_delete'
    id: string
    approved: boolean
    action: string
    extra?: any
  } | null>(null)

  const [showUserManager, setShowUserManager] = useState(false)

  // Order management with admin verification
  const requestOrderApproval = (orderId: string, approved: boolean) => {
    console.log('🛒 Requesting order approval:', { orderId, approved })
    const action = approved ? 'Bestellung genehmigen' : 'Bestellung ablehnen'
    setPendingAction({ type: 'order', id: orderId, approved, action })
    setShowAdminVerification(true)
    console.log('📋 Set pending order action and showing admin verification')
  }

  const handleOrderApproval = (orderId: string, approved: boolean) => {
    console.log('🎯 Handling order approval:', { orderId, approved })
    const status = approved ? 'approved' : 'rejected'
    const comment = approved ? 'Vom Administrator genehmigt' : 'Vom Administrator abgelehnt'
    
    updateOrderStatus(orderId, status, {
      approverName: 'Administrator',
      approvalComment: comment
    })
    console.log('✅ Order status updated')
  }

  // Bus management with admin verification
  const requestBusApproval = (reservationId: string, approved: boolean) => {
    console.log('🚌 Requesting bus approval:', { reservationId, approved })
    const action = approved ? 'Bus-Reservierung genehmigen' : 'Bus-Reservierung ablehnen'
    setPendingAction({ type: 'bus', id: reservationId, approved, action })
    setShowAdminVerification(true)
    console.log('📋 Set pending bus action and showing admin verification')
  }

  const handleBusApproval = (reservationId: string, approved: boolean) => {
    console.log('🎯 Handling bus approval:', { reservationId, approved })
    const status = approved ? 'approved' : 'rejected'
    updateReservationStatus(reservationId, status)
    console.log('✅ Bus reservation status updated')
  }

  // Testspiel management with admin verification
  const requestTestspielApproval = (testspielId: string, approved: boolean) => {
    console.log('🚀 Requesting testspiel approval:', { testspielId, approved })
    console.log('📊 Current testspiele state:', testspiele.length, 'total testspiele')
    const action = approved ? 'Testspiel bestätigen' : 'Testspiel ablehnen'
    setPendingAction({ type: 'testspiel', id: testspielId, approved, action })
    setShowAdminVerification(true)
    console.log('📋 Set pending action and showing admin verification')
  }

  const handleTestspielApproval = async (testspielId: string, approved: boolean) => {
    console.log('🎯 Handling testspiel approval:', { testspielId, approved })
    const status = approved ? 'confirmed' : 'cancelled'
    
    try {
      const result = await updateTestspielStatus(testspielId, status)
      console.log('✅ Update result:', result)
      
      if (result.error) {
        console.error('❌ Error updating testspiel:', result.error)
        alert(`Fehler: ${result.error}`)
      } else {
        console.log('🔄 Refreshing testspiele list...')
        await fetchTestspiele()
        console.log('✅ Testspiel status updated successfully!')
      }
    } catch (error) {
      console.error('❌ Unexpected error:', error)
      alert('Unerwarteter Fehler beim Aktualisieren des Testspiel-Status')
    }
  }

  // User management with admin verification
  const requestUserApproval = (userId: string, approved: boolean) => {
    const action = approved ? 'Benutzer genehmigen' : 'Benutzer ablehnen'
    setPendingAction({ type: 'user', id: userId, approved, action })
    setShowAdminVerification(true)
  }

  const requestRoleChange = (userId: string, newRole: 'admin' | 'trainer') => {
    const user = users.find(u => u.id === userId)
    const action = `Rolle zu ${newRole === 'admin' ? 'Administrator' : 'Trainer'} ändern`
    setPendingAction({ 
      type: 'role_change', 
      id: userId, 
      approved: true, 
      action,
      extra: { newRole, userName: user?.name || user?.email }
    })
    setShowAdminVerification(true)
  }

  const requestUserDeletion = (userId: string) => {
    const user = users.find(u => u.id === userId)
    const action = `Benutzer ${user?.name || user?.email} löschen`
    setPendingAction({ 
      type: 'user_delete', 
      id: userId, 
      approved: true, 
      action,
      extra: { userName: user?.name || user?.email }
    })
    setShowAdminVerification(true)
  }

  // User management
  const handleUserApprove = async (userId: string) => {
    try {
      const result = await approveUser(userId)
      if (result.error) {
        alert(`Fehler: ${result.error}`)
      } else {
        await fetchUsers() // Refresh list
      }
    } catch (error) {
      console.error('Error approving user:', error)
      alert('Fehler beim Genehmigen des Benutzers')
    }
  }

  const handleUserReject = async (userId: string) => {
    try {
      const result = await rejectUser(userId)
      if (result.error) {
        alert(`Fehler: ${result.error}`)
      } else {
        await fetchUsers() // Refresh list
      }
    } catch (error) {
      console.error('Error rejecting user:', error)
      alert('Fehler beim Ablehnen des Benutzers')
    }
  }

  const handleRoleChange = async (userId: string, newRole: 'admin' | 'trainer') => {
    try {
      const result = await changeUserRole(userId, newRole)
      if (result.error) {
        alert(`Fehler: ${result.error}`)
      } else {
        await fetchUsers() // Refresh list
        alert(`Rolle erfolgreich zu ${newRole === 'admin' ? 'Administrator' : 'Trainer'} geändert`)
      }
    } catch (error) {
      console.error('Error changing user role:', error)
      alert('Fehler beim Ändern der Benutzerrolle')
    }
  }

  const handleUserDelete = async (userId: string) => {
    try {
      const result = await deleteUser(userId)
      if (result.error) {
        alert(`Fehler: ${result.error}`)
      } else {
        await fetchUsers() // Refresh list
        alert('Benutzer erfolgreich gelöscht')
      }
    } catch (error) {
      console.error('Error deleting user:', error)
      alert('Fehler beim Löschen des Benutzers')
    }
  }

  // Handle successful admin verification
  const handleAdminVerificationSuccess = () => {
    console.log('🔑 Admin verification successful, executing pending action:', pendingAction)
    if (!pendingAction) return

    switch (pendingAction.type) {
      case 'order':
        handleOrderApproval(pendingAction.id, pendingAction.approved)
        break
      case 'bus':
        handleBusApproval(pendingAction.id, pendingAction.approved)
        break
      case 'testspiel':
        console.log('⚽ Executing testspiel approval...')
        handleTestspielApproval(pendingAction.id, pendingAction.approved)
        break
      case 'user':
        if (pendingAction.approved) {
          handleUserApprove(pendingAction.id)
        } else {
          handleUserReject(pendingAction.id)
        }
        break
      case 'role_change':
        if (pendingAction.extra?.newRole) {
          handleRoleChange(pendingAction.id, pendingAction.extra.newRole)
        }
        break
      case 'user_delete':
        handleUserDelete(pendingAction.id)
        break
    }

    setShowAdminVerification(false)
    setPendingAction(null)
  }

  const handleAdminVerificationCancel = () => {
    setShowAdminVerification(false)
    setPendingAction(null)
  }

  return (
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '16px 16px 32px 16px',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)'
      }}>
        <div style={{
          marginBottom: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            flex: '1'
          }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              letterSpacing: '-0.025em'
            }}>
              Admin-Bereich
            </h1>
            <p style={{
              color: '#6B7280',
              fontSize: '16px',
              fontWeight: '500'
            }}>
              Systemverwaltung und Benutzerverwaltung
            </p>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%'
          }}>
            {/* Debug buttons removed as requested */}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '20px'
        }}>
          {/* Testspiele verwalten */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)'
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}
          >
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Calendar style={{ width: '24px', height: '24px', color: '#16A34A' }} />
              </div>
              <div style={{ flex: '1' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '4px',
                  letterSpacing: '-0.025em'
                }}>
                  Testspiele
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  {pendingTestspiele.length} wartende Bestätigungen
                </p>
              </div>
              {pendingTestspiele.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '700',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  minWidth: '24px',
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  animation: 'pulse 2s infinite'
                }}>
                  {pendingTestspiele.length}
                </div>
              )}
            </div>

            {pendingTestspiele.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '32px 16px',
                color: '#6B7280'
              }}>
                <Calendar style={{
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 12px',
                  color: '#D1D5DB'
                }} />
                <p style={{
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Keine ausstehenden Testspiele
                </p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {pendingTestspiele.slice(0, 3).map(testspiel => (
                  <div key={testspiel.id} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    borderRadius: '12px',
                    border: '1px solid #f3e8ff',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ flex: '1' }}>
                      <div style={{
                        fontWeight: '600',
                        color: '#1F2937',
                        marginBottom: '4px',
                        fontSize: '14px'
                      }}>
                        {testspiel.team} vs. {testspiel.opponent}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        fontWeight: '500'
                      }}>
                        {new Date(testspiel.date).toLocaleDateString('de-CH')} • {testspiel.time} Uhr
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      width: '100%'
                    }}>
                      <button
                        onClick={() => requestTestspielApproval(testspiel.id, true)}
                        style={{
                          background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          flex: '1',
                          minWidth: '90px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)',
                          transform: 'translateY(0)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)'
                          e.currentTarget.style.transform = 'translateY(-1px)'
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(22, 163, 74, 0.4)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(22, 163, 74, 0.3)'
                        }}
                        title="Testspiel bestätigen"
                      >
                        <Check style={{ width: '14px', height: '14px' }} />
                        Bestätigen
                      </button>
                      <button
                        onClick={() => requestTestspielApproval(testspiel.id, false)}
                        style={{
                          background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          flex: '1',
                          minWidth: '90px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
                          transform: 'translateY(0)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)'
                          e.currentTarget.style.transform = 'translateY(-1px)'
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.4)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.3)'
                        }}
                        title="Testspiel ablehnen"
                      >
                        <X style={{ width: '14px', height: '14px' }} />
                        Ablehnen
                      </button>
                    </div>
                  </div>
                ))}
                {pendingTestspiele.length > 3 && (
                  <p style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontStyle: 'italic'
                  }}>
                    ...und {pendingTestspiele.length - 3} weitere
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Bestellungen verwalten */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)'
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}
          >
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #dc2626 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(220, 38, 38, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ShoppingCart style={{ width: '24px', height: '24px', color: '#DC2626' }} />
              </div>
              <div style={{ flex: '1' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '4px',
                  letterSpacing: '-0.025em'
                }}>
                  Bestellungen
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  {pendingOrders.length} wartende Genehmigungen
                </p>
              </div>
              {pendingOrders.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '700',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  minWidth: '24px',
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  animation: 'pulse 2s infinite'
                }}>
                  {pendingOrders.length}
                </div>
              )}
            </div>

            {pendingOrders.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '32px 16px',
                color: '#6B7280'
              }}>
                <ShoppingCart style={{
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 12px',
                  color: '#D1D5DB'
                }} />
                <p style={{
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Keine ausstehenden Bestellungen
                </p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {pendingOrders.slice(0, 3).map(order => (
                  <div key={order.id} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    borderRadius: '12px',
                    border: '1px solid #f3e8ff',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ flex: '1' }}>
                      <div style={{
                        fontWeight: '600',
                        color: '#1F2937',
                        marginBottom: '4px',
                        fontSize: '14px'
                      }}>
                        Bestellung #{order.id.slice(-8)}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        fontWeight: '500'
                      }}>
                        {order.orderedBy} - {order.team}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      width: '100%'
                    }}>
                      <button
                        onClick={() => requestOrderApproval(order.id, true)}
                        style={{
                          background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          flex: '1',
                          minWidth: '90px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)',
                          transform: 'translateY(0)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)'
                          e.currentTarget.style.transform = 'translateY(-1px)'
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(22, 163, 74, 0.4)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(22, 163, 74, 0.3)'
                        }}
                        title="Bestellung genehmigen"
                      >
                        <Check style={{ width: '14px', height: '14px' }} />
                        Genehmigen
                      </button>
                      <button
                        onClick={() => requestOrderApproval(order.id, false)}
                        style={{
                          background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          flex: '1',
                          minWidth: '90px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
                          transform: 'translateY(0)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)'
                          e.currentTarget.style.transform = 'translateY(-1px)'
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.4)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.3)'
                        }}
                        title="Bestellung ablehnen"
                      >
                        <X style={{ width: '14px', height: '14px' }} />
                        Ablehnen
                      </button>
                    </div>
                  </div>
                ))}
                {pendingOrders.length > 3 && (
                  <p style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontStyle: 'italic'
                  }}>
                    ...und {pendingOrders.length - 3} weitere
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Bus-Reservierungen verwalten */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)'
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}
          >
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #2563eb 0%, #3b82f6 50%, #2563eb 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Bus style={{ width: '24px', height: '24px', color: '#2563EB' }} />
              </div>
              <div style={{ flex: '1' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '4px',
                  letterSpacing: '-0.025em'
                }}>
                  Bus-Reservierungen
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  {pendingBusReservations.length} wartende Genehmigungen
                </p>
              </div>
              {pendingBusReservations.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '700',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  minWidth: '24px',
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  animation: 'pulse 2s infinite'
                }}>
                  {pendingBusReservations.length}
                </div>
              )}
            </div>

            {pendingBusReservations.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '32px 16px',
                color: '#6B7280'
              }}>
                <Bus style={{
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 12px',
                  color: '#D1D5DB'
                }} />
                <p style={{
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Keine ausstehenden Reservierungen
                </p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {pendingBusReservations.slice(0, 3).map(reservation => (
                  <div key={reservation.id} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
                    borderRadius: '12px',
                    border: '1px solid #e1f5fe',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ flex: '1' }}>
                      <div style={{
                        fontWeight: '600',
                        color: '#1F2937',
                        marginBottom: '4px',
                        fontSize: '14px'
                      }}>
                        {reservation.team}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        fontWeight: '500'
                      }}>
                        {reservation.destination} - {reservation.purpose}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      width: '100%'
                    }}>
                      <button
                        onClick={() => requestBusApproval(reservation.id, true)}
                        style={{
                          background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          flex: '1',
                          minWidth: '90px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)',
                          transform: 'translateY(0)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)'
                          e.currentTarget.style.transform = 'translateY(-1px)'
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(22, 163, 74, 0.4)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(22, 163, 74, 0.3)'
                        }}
                        title="Bus-Reservierung genehmigen"
                      >
                        <Check style={{ width: '14px', height: '14px' }} />
                        Genehmigen
                      </button>
                      <button
                        onClick={() => requestBusApproval(reservation.id, false)}
                        style={{
                          background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          flex: '1',
                          minWidth: '90px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
                          transform: 'translateY(0)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)'
                          e.currentTarget.style.transform = 'translateY(-1px)'
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.4)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.3)'
                        }}
                        title="Bus-Reservierung ablehnen"
                      >
                        <X style={{ width: '14px', height: '14px' }} />
                        Ablehnen
                      </button>
                    </div>
                  </div>
                ))}
                {pendingBusReservations.length > 3 && (
                  <p style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontStyle: 'italic'
                  }}>
                    ...und {pendingBusReservations.length - 3} weitere
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Benutzerverwaltung */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)'
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}
          >
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #f59e0b 0%, #f97316 50%, #f59e0b 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(245, 158, 11, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users style={{ width: '24px', height: '24px', color: '#F59E0B' }} />
              </div>
              <div style={{ flex: '1' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '4px',
                  letterSpacing: '-0.025em'
                }}>
                  Benutzerverwaltung
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  {pendingUsers.length} wartende • {approvedUsers.length} aktive Benutzer
                </p>
              </div>
              {pendingUsers.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '700',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  minWidth: '24px',
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  animation: 'pulse 2s infinite'
                }}>
                  {pendingUsers.length}
                </div>
              )}
            </div>

            {usersLoading ? (
              <div style={{
                textAlign: 'center',
                padding: '32px 16px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #f3f4f6',
                  borderTop: '3px solid #f59e0b',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 12px'
                }}></div>
                <p style={{
                  color: '#6B7280',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Lade Benutzer...
                </p>
              </div>
            ) : !showUserManager ? (
              <div style={{
                textAlign: 'center',
                padding: '32px 16px'
              }}>
                <Users style={{
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 16px',
                  color: '#F59E0B'
                }} />
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <p style={{
                    color: '#1F2937',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {pendingUsers.length} wartende Genehmigungen
                  </p>
                  <p style={{
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {approvedUsers.length} aktive Benutzer verwalten
                  </p>
                </div>
                <button 
                  onClick={() => setShowUserManager(true)}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '14px 20px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)',
                    transform: 'translateY(0)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 8px 25px -5px rgba(245, 158, 11, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(245, 158, 11, 0.3)'
                  }}
                >
                  <Users style={{ width: '16px', height: '16px' }} />
                  Benutzer verwalten
                </button>
              </div>
            ) : (
              <div>
                {/* Pending users first */}
                {pendingUsers.length > 0 && (
                  <div style={{
                    marginBottom: '24px'
                  }}>
                    <h4 style={{
                      fontWeight: '600',
                      marginBottom: '16px',
                      color: '#1F2937',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#F59E0B',
                        animation: 'pulse 2s infinite'
                      }}></div>
                      Wartende Genehmigungen
                    </h4>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {pendingUsers.map(user => (
                        <div key={user.id} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          padding: '16px',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          borderRadius: '12px',
                          border: '1px solid #fef3c7',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ flex: '1' }}>
                            <div style={{
                              fontWeight: '600',
                              color: '#1F2937',
                              marginBottom: '4px',
                              fontSize: '14px'
                            }}>
                              {user.name || 'Unbekannt'}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              fontWeight: '500'
                            }}>
                              {user.email} • {user.team || 'Kein Team'}
                            </div>
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: '8px',
                            width: '100%'
                          }}>
                            <button
                              onClick={() => requestUserApproval(user.id, true)}
                              disabled={usersLoading}
                              style={{
                                background: usersLoading ? '#9CA3AF' : 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '10px 16px',
                                borderRadius: '10px',
                                cursor: usersLoading ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                flex: '1',
                                minWidth: '90px',
                                transition: 'all 0.2s ease',
                                boxShadow: usersLoading ? '0 2px 4px rgba(156, 163, 175, 0.3)' : '0 2px 4px rgba(22, 163, 74, 0.3)',
                                transform: 'translateY(0)',
                                opacity: usersLoading ? '0.6' : '1'
                              }}
                              title="Benutzer genehmigen"
                            >
                              <Check style={{ width: '14px', height: '14px' }} />
                              Genehmigen
                            </button>
                            <button
                              onClick={() => requestUserApproval(user.id, false)}
                              disabled={usersLoading}
                              style={{
                                background: usersLoading ? '#9CA3AF' : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '10px 16px',
                                borderRadius: '10px',
                                cursor: usersLoading ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                flex: '1',
                                minWidth: '90px',
                                transition: 'all 0.2s ease',
                                boxShadow: usersLoading ? '0 2px 4px rgba(156, 163, 175, 0.3)' : '0 2px 4px rgba(220, 38, 38, 0.3)',
                                transform: 'translateY(0)',
                                opacity: usersLoading ? '0.6' : '1'
                              }}
                              title="Benutzer ablehnen"
                            >
                              <X style={{ width: '14px', height: '14px' }} />
                              Ablehnen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approved users management */}
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{
                      fontWeight: '600',
                      color: '#1F2937',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#16A34A'
                      }}></div>
                      Aktive Benutzer ({approvedUsers.length})
                    </h4>
                    <button
                      onClick={() => setShowUserManager(false)}
                      style={{
                        padding: '8px',
                        background: 'linear-gradient(135deg, #6B7280 0%, #9CA3AF 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Schließen"
                    >
                      <X style={{ width: '14px', height: '14px' }} />
                      Schließen
                    </button>
                  </div>
                  
                  {approvedUsers.length === 0 ? (
                    <p style={{
                      color: '#6B7280',
                      fontSize: '14px',
                      textAlign: 'center',
                      padding: '20px',
                      fontStyle: 'italic'
                    }}>
                      Keine aktiven Benutzer
                    </p>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      maxHeight: '320px',
                      overflowY: 'auto',
                      padding: '4px'
                    }}>
                      {approvedUsers.map(user => (
                        <div key={user.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          border: '1px solid #bbf7d0',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            flex: '1'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              flexWrap: 'wrap'
                            }}>
                              <span style={{
                                fontWeight: '600',
                                color: '#1F2937'
                              }}>
                                {user.name || 'Unbekannt'}
                              </span>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                ...(user.role === 'admin' ? {
                                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                  color: '#d97706'
                                } : {
                                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                  color: '#2563eb'
                                })
                              }}>
                                {user.role === 'admin' ? 'Admin' : 'Trainer'}
                              </span>
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '12px',
                              color: '#6B7280'
                            }}>
                              <span>{user.email}</span>
                              {user.team && (
                                <>
                                  <span>•</span>
                                  <span style={{
                                    background: '#f3f4f6',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontWeight: '500'
                                  }}>
                                    {user.team}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: '6px'
                          }}>
                            {user.role === 'trainer' && (
                              <button
                                onClick={() => requestRoleChange(user.id, 'admin')}
                                disabled={usersLoading}
                                style={{
                                  padding: '6px 8px',
                                  background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: usersLoading ? 'not-allowed' : 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  transition: 'all 0.2s ease',
                                  opacity: usersLoading ? '0.6' : '1'
                                }}
                                title="Zu Admin befördern"
                              >
                                👑 Admin
                              </button>
                            )}
                            {user.role === 'admin' && (
                              <button
                                onClick={() => requestRoleChange(user.id, 'trainer')}
                                disabled={usersLoading}
                                style={{
                                  padding: '6px 8px',
                                  background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: usersLoading ? 'not-allowed' : 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  transition: 'all 0.2s ease',
                                  opacity: usersLoading ? '0.6' : '1'
                                }}
                                title="Zu Trainer herabstufen"
                              >
                                👥 Trainer
                              </button>
                            )}
                            <button
                              onClick={() => requestUserDeletion(user.id)}
                              disabled={usersLoading}
                              style={{
                                padding: '6px',
                                color: '#dc2626',
                                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                                border: '1px solid #fecaca',
                                borderRadius: '6px',
                                cursor: usersLoading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: usersLoading ? '0.6' : '1'
                              }}
                              title="Benutzer löschen"
                            >
                              <Trash2 style={{ width: '12px', height: '12px' }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Dateiablage - Simple Navigation */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)'
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}
          >
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #7c3aed 0%, #8b5cf6 50%, #7c3aed 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(124, 58, 237, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FileText style={{ width: '24px', height: '24px', color: '#7C3AED' }} />
              </div>
              <div style={{ flex: '1' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '4px',
                  letterSpacing: '-0.025em'
                }}>
                  Dateiablage
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  Dateien zentral verwalten
                </p>
              </div>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '32px 16px'
            }}>
              <FileText style={{
                width: '48px',
                height: '48px',
                margin: '0 auto 16px',
                color: '#A855F7'
              }} />
              <p style={{
                color: '#6B7280',
                marginBottom: '16px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Verwalten Sie alle Vereinsdokumente und Dateien in der dedizierten Dateiablage.
              </p>
              <button 
                onClick={() => navigate('/dateiablage')}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.3)',
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #6D28D9 0%, #7C3AED 100%)'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 8px 25px -5px rgba(124, 58, 237, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)'
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(124, 58, 237, 0.3)'
                }}
              >
                Zur Dateiablage
              </button>
            </div>
          </div>

          {/* Store Manager - Active */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)'
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}
          >
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #f59e0b 0%, #f97316 50%, #f59e0b 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(245, 158, 11, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Package style={{ width: '24px', height: '24px', color: '#F59E0B' }} />
              </div>
              <div style={{ flex: '1' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '4px',
                  letterSpacing: '-0.025em'
                }}>
                  Store Manager
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  Equipment verwalten
                </p>
              </div>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '32px 16px'
            }}>
              <Package style={{
                width: '48px',
                height: '48px',
                margin: '0 auto 16px',
                color: '#F59E0B'
              }} />
              <p style={{
                color: '#6B7280',
                marginBottom: '16px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Verwalten Sie Equipment, Kategorien und Lagerbestände zentral über den Store Manager.
              </p>
              <button 
                onClick={() => navigate('/store-manager')}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)',
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 8px 25px -5px rgba(245, 158, 11, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)'
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(245, 158, 11, 0.3)'
                }}
              >
                Zum Store Manager
              </button>
            </div>
          </div>
        </div>

        {/* Admin Code Verification Modal */}
        {showAdminVerification && pendingAction && (
          <AdminCodeVerification
            onSuccess={handleAdminVerificationSuccess}
            onCancel={handleAdminVerificationCancel}
            action={pendingAction.action}
          />
        )}
      </div>
  )
}
